// ── Role-Based Access Control (menu + route scoping) ────────────────────────────
// Scoped roles only see a subset of the app. Non-scoped roles (super_admin, admin,
// user, approver, trainer) are unaffected here and keep their existing admin/superAdmin
// item filtering in Layout.

// Top-level menu group codes a scoped role may see (Dashboard "DASH" handled separately).
export const MENU_ACCESS = {
  logistics: ["DSP", "LOG", "FAL"],         // Dispatch + Logistics + FA-Logistics
  po_dispatch: ["ORD", "DSP"],              // POs (Orders) + Dispatch
  manager: ["ORD", "DSP", "INS", "SET", "FCT", "FAL"],    // + Insights + custom Setup + Forecasts + FA
};

// For roles with a restricted Setup menu — the exact Setup item paths allowed.
export const SETUP_ITEM_ACCESS = {
  manager: ["/transfer-pricing", "/customers", "/train", "/users"],
};

// Menu items restricted to a specific role allow-list (Admin & Super Admin are ALWAYS allowed).
// Applies to the nav menu AND the route guard. Path = first URL segment with a leading slash.
export const ITEM_ROLE_ACCESS = {
  "/cfs-cutoff": ["logistics", "manager"],   // CFS Cut-Off → Logistics + Manager + Admin + Super Admin. Non-admins get the planning view (Must Ship By + filter, no cut-off dates).
};

// Can a role see/open a role-restricted menu item? Admin/Super Admin always may.
export function canSeeItem(role, path) {
  const allow = ITEM_ROLE_ACCESS[path];
  if (!allow) return true;
  return role === "admin" || role === "super_admin" || allow.includes(role);
}

// Scoped roles that do NOT get the Dashboard home page (land on their first screen instead).
export const NO_DASHBOARD_ROLES = ["logistics", "po_dispatch"];

// Where each scoped role lands after login / when hitting a disallowed page.
export const ROLE_HOME = {
  logistics: "/dispatch-register",
  po_dispatch: "/pos",
  finance: "/fa-logistics",
};

export const SCOPED_ROLES = ["logistics", "po_dispatch", "manager"];

// First URL path segment → menu area code.
const SEG_AREA = {
  "": "DASH",
  "pos": "ORD", "po": "ORD", "orders": "ORD", "intake-review": "ORD",
  "intake-audit": "ORD", "open-orders": "ORD", "failures": "ORD",
  "dispatch-register": "DSP", "dispatch-plan": "DSP", "dispatch-plans": "DSP",
  "dispatch": "DSP", "cfs-cutoff": "DSP", "shipping": "DSP", "other-charges": "DSP",
  "logistics": "LOG",
  "fa-logistics": "FAL",
  "otd-risk": "INS", "explorer": "INS", "ship-window": "INS", "sales": "INS", "short-closed": "INS",
  "reports": "INS", "forecasts": "FCT",
  "transfer-pricing": "SET", "customers": "SET", "train": "SET", "users": "SET",
  "cockpit": "SET", "data-health": "SET", "import": "SET", "portal-push": "SET",
  "reconcile": "SET", "inbox": "SET", "settings": "SET", "adminx": "SET",
};

export function areaForPath(pathname) {
  const seg = (pathname || "/").split("/")[1] || "";
  return SEG_AREA[seg]; // undefined for unknown/utility routes
}

export function isScopedRole(role) {
  return SCOPED_ROLES.includes(role);
}

// Can a role see a top-level nav group (by its code)?
export function canSeeGroup(role, code) {
  if (code === "DASH") return !NO_DASHBOARD_ROLES.includes(role);
  const allow = MENU_ACCESS[role];
  if (!allow) return true; // non-scoped role
  return allow.includes(code);
}

// Can a scoped role open a given app path? (used by the route guard)
export function canAccessPath(role, pathname) {
  // Item-level role restriction applies to ALL roles (incl. non-scoped). Admin/Super Admin exempt.
  const seg0 = "/" + ((pathname || "").split("/")[1] || "");
  if (ITEM_ROLE_ACCESS[seg0] && !canSeeItem(role, seg0)) return false;
  const area = areaForPath(pathname);
  if (!area) return true; // unknown/utility route — don't block
  if (area === "DASH") return !NO_DASHBOARD_ROLES.includes(role);
  const allow = MENU_ACCESS[role];
  if (!allow) return true; // non-scoped role
  if (!allow.includes(area)) return false;
  if (area === "SET" && SETUP_ITEM_ACCESS[role]) {
    const seg = "/" + ((pathname || "").split("/")[1] || "");
    return SETUP_ITEM_ACCESS[role].includes(seg);
  }
  return true;
}

export function roleHome(role) {
  return ROLE_HOME[role] || "/";
}

// Human labels + badge colors for every role (used by the top-bar badge and User Admin).
export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  logistics: "Logistics",
  po_dispatch: "PO & Dispatch",
  finance: "Finance",
  approver: "Approver",
  trainer: "Trainer",
  user: "User",
};

export const ROLE_BADGE = {
  super_admin: { bg: "#7C3AED", fg: "#FFFFFF" },
  admin: { bg: "#2563EB", fg: "#FFFFFF" },
  manager: { bg: "#0891B2", fg: "#FFFFFF" },
  logistics: { bg: "#C2410C", fg: "#FFFFFF" },
  po_dispatch: { bg: "#15803D", fg: "#FFFFFF" },
  finance: { bg: "#9333EA", fg: "#FFFFFF" },
  approver: { bg: "#0D9488", fg: "#FFFFFF" },
  trainer: { bg: "#A16207", fg: "#FFFFFF" },
  user: { bg: "#475569", fg: "#FFFFFF" },
};

export function roleLabel(role) {
  return ROLE_LABELS[role] || role || "User";
}

export function roleBadge(role) {
  return ROLE_BADGE[role] || ROLE_BADGE.user;
}
